import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild
}                                                                     from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {map}                                                          from 'rxjs/operators';
import {
  ApDynComponentComponent
}                                                                     from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynformsConfigFieldset
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynGridPagerConfigBase
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {ApUtilService}                                                from '../../ap-utils/service/ap-util.service';
import {FieldStore}                                                   from '../../stores/farm/field.store';
import {LanguageStore}              from '../../stores/translation/language.store';
import {FormStore, IEntryComponent} from '../../stores/layout/form.store';
import {
  FieldWorkPlanBooksStore
}                                   from '../../stores/field-work/field-work-plan-books.store';
import {WorkTypesStore}                                               from '../../stores/base-data/worrktypes.store';
import {DriverStore}                                                  from '../../stores/docu/driver.store';
import {MachineStore}                                                 from '../../stores/docu/machine.store';
import {InstrumentStore}                                              from '../../stores/docu/instrument.store';
import {CampaignYearStore}                                            from '../../stores/login/campaignyear.store';
import {SettingsStore}                                                from '../../stores/base-data/settings.store';
import {WorktypeUnitStore} from '../../stores/base-data/worktype-unit.store';
import {
  FwPlanPlanningDetailsProvider
} from './plan-planning/providers/fw-plan-planning-details.provider';
import {
  FwPlanPlanningOverviewProvider
} from './plan-planning/providers/fw-plan-planning-overview.provider';
import {
  FwPlanPlanningSelectionProvider
} from './plan-planning/providers/fw-plan-planning-selection.provider';
import {
  PpWizardStates
} from '../plant-protection-entry-components/planning-wizard/pp-planning-wizard.types';
import {
  FwPlanningWizardStates,
  IFwPlanPlanningWizardDetailsData,
  IFwPlanPlanningWizardOverviewData,
  IFwPlanPlanningWizardProvider,
  IFwPlanPlanningWizardSelectionData
} from './plan-planning/fw-plan-planning.types';

@Component({
  selector: 'fw-plan-planning',
  template: `
    <ap-dyncomponent [headerIcon]="'ap-fungidetect-icon'"
                     [loading$]="loading$"
                     [columns]="columns$ | async"
                     [fieldSets]="fieldSets$ | async"
                     [errorKeys]="errorKeys$ | async"
                     [canCreate]="false"
                     [canSearch]="true"
                     [items]="items$ | async"
                     [pager]="pager">
      <div class="ap-form-actions" dynforms.action>
        <div apResponsive
             [sizeMd]="'25-100'"
             [style.text-align]="'left'">
          <button kendoButton
                  [id]="'button_prev'"
                  [type]="'button'"
                  [class]="'button-important'"
                  (apSingleClick)="handleStateChangeAction.emit(wizardSteps.Selection)">
            {{'Global_Back_With_Arrow' | translate }}
          </button>
        </div>
        <div apResponsive
             [sizeMd]="'50-100'"
             [style.text-align]="'center'">
          <ap-breadcrumbs-steper [columns]="breadCrumbsColumns"
                                 [selected]="1"
          ></ap-breadcrumbs-steper>
        </div>
        <div apResponsive
             [sizeMd]="'25-100'"
             [style.text-align]="'right'">
          <button kendoButton
                  [id]="'button_next'"
                  [disabled]="!(dynComponent.Valid$ | async)"
                  [type]="'button'"
                  [themeColor]="'primary'"
                  [class]="'button-important'"
                  (apSingleClick)="dynComponent?.dynForm?.form?.valid && handleStateChangeAction.emit(wizardSteps.Overview)">
            {{'Global_Next_With_Arrow' | translate }}
          </button>
        </div>
      </div>
    </ap-dyncomponent>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FwPlanPlanningComponent implements OnInit, AfterContentInit, OnDestroy, IEntryComponent {
  /**
   * Implements IEntryComponent
   */
  public Caption$: BehaviorSubject<string> = new BehaviorSubject('');
  @ViewChild(ApDynComponentComponent, {static: true})
  public dynComponent: ApDynComponentComponent;

  public wizardSteps = FwPlanningWizardStates;
  public errorKeys$ = new BehaviorSubject<string[]>([]);
  public columns$: Observable<ApDynGridColumnConfigBase[]> = of([]);
  public fieldSets$ = new BehaviorSubject<ApDynformsConfigFieldset[]>([]);
  public items$ = new Observable<IFwPlanPlanningWizardSelectionData[] | IFwPlanPlanningWizardOverviewData[]>(undefined);
  public pager: ApDynGridPagerConfigBase;
  public loading$: Observable<boolean>;
  public handleStateChangeAction: EventEmitter<any>;
  public selectionData: IFwPlanPlanningWizardSelectionData[];
  public detailsData: IFwPlanPlanningWizardDetailsData;
  public overviewData: IFwPlanPlanningWizardOverviewData[];

  private _subscriptions: Subscription[] = [];
  private _stateModel: { [oldState: string]: { [newState: string]: (oldState: FwPlanningWizardStates) => void } } = {};
  private _activeWizardStep: FwPlanningWizardStates = FwPlanningWizardStates.Selection;

  public breadCrumbsColumns: FwPlanningWizardStates[] = [
    FwPlanningWizardStates.Selection,
    FwPlanningWizardStates.Details,
    FwPlanningWizardStates.Overview
  ];

  constructor(private formStore: FormStore,
              private fieldStore: FieldStore,
              private driverStore: DriverStore,
              private machineStore: MachineStore,
              private languageStore: LanguageStore,
              private settingsStore: SettingsStore,
              private workTypeStore: WorkTypesStore,
              private instrumentStore: InstrumentStore,
              private campaignYearStore: CampaignYearStore,
              private workTypeUnitStore: WorktypeUnitStore,
              private fieldWorkPlanBooksStore: FieldWorkPlanBooksStore,
              private wizardSelectionProvider: FwPlanPlanningSelectionProvider,
              private wizardDetailsProvider: FwPlanPlanningDetailsProvider,
              private wizardOverviewProvider: FwPlanPlanningOverviewProvider) {
  }

  /**
   * Initializes the component by setting up state change action and state model transitions
   */
  public ngOnInit(): void {
    this.handleStateChangeAction = ApUtilService.createEventEmitter(this.handleStateChange.bind(this), this._subscriptions);
    // Initialize empty state objects for each wizard state
    this._stateModel[FwPlanningWizardStates.Cancel] = {};
    this._stateModel[FwPlanningWizardStates.Selection] = {};
    this._stateModel[FwPlanningWizardStates.Details] = {};
    this._stateModel[FwPlanningWizardStates.Overview] = {};
    this._stateModel[FwPlanningWizardStates.Save] = {};
    // Define state transition actions
    this._stateModel[FwPlanningWizardStates.Selection][FwPlanningWizardStates.Details] = this.initDetails.bind(this); // user continues from selection to details
    this._stateModel[FwPlanningWizardStates.Details][FwPlanningWizardStates.Overview] = this.initOverview.bind(this); // a user switches from details to overview-tab
    this._stateModel[FwPlanningWizardStates.Overview][FwPlanningWizardStates.Save] = this.initSave.bind(this); // user clicks export in overview
    this._stateModel[FwPlanningWizardStates.Overview][FwPlanningWizardStates.Details] = this.initDetails.bind(this); // the user goes back to details from overview
    this._stateModel[FwPlanningWizardStates.Details][FwPlanningWizardStates.Selection] = this.initSelection.bind(this); // user navigates from details back to selection
    this._stateModel[FwPlanningWizardStates.Selection][FwPlanningWizardStates.Cancel] = this.initLeave.bind(this); // leaving wizard
    this._stateModel[FwPlanningWizardStates.Save][FwPlanningWizardStates.Details] = this.initDetails.bind(this); // after failed export user goes back to details
    this._stateModel[FwPlanningWizardStates.Save][FwPlanningWizardStates.Save] = this.initSave.bind(this); // export retry by user
  }

  /**
   * Initializes component after content is ready by setting up loading state,
   * wizard providers and form value change subscription
   */
  public ngAfterContentInit(): void {
    this.loading$ = combineLatest([
      this.fieldStore.Loading$,
      this.driverStore.Loading$,
      this.machineStore.Loading$,
      this.languageStore.Loading$,
      this.settingsStore.Loading$,
      this.workTypeStore.Loading$,
      this.instrumentStore.Loading$,
      this.campaignYearStore.Loading$,
      this.workTypeUnitStore.Loading$
    ]).pipe(
      map((loadings) => loadings.some((l) => l))
    );

    // Initialize wizard providers with required dependencies
    this.wizardSelectionProvider.init(
      this.Caption$,
      this.fieldSets$,
      this.handleStateChangeAction,
      this.dynComponent);

    this.wizardDetailsProvider.init(
      this.Caption$,
      this.fieldSets$,
      this.dynComponent.dynForm);

    this.wizardOverviewProvider.init(
      this.Caption$,
      this.fieldSets$,
      this.handleStateChangeAction,
      this.dynComponent);

    // Subscribe to form value changes in a details step
    this._subscriptions.push(
      this.dynComponent.dynForm.formValueChanges.subscribe(valuesChangesArgs => {
        if (this._activeWizardStep !== FwPlanningWizardStates.Details
          || this.dynComponent.dynForm.form.pristine ||
          (!this.dynComponent.dynForm.form.dirty && !this.dynComponent.dynForm.form.touched)) {
          return;
        }
        this.wizardDetailsProvider.onFormValueChanges(valuesChangesArgs);
      })
    );

    this.initSelection(PpWizardStates.Initial);
  }

  /**
   * Cleans up component by unsubscribing and destroying wizard providers
   */
  public ngOnDestroy(): void {
    this._subscriptions.forEach(d => d.unsubscribe());
    this.wizardSelectionProvider.onDestroy();
    this.wizardDetailsProvider.onDestroy();
    this.wizardOverviewProvider.onDestroy();
  }

  /**
   * Handles state changes in the wizard by saving current state data and executing state transition actions
   */
  private handleStateChange(newState: FwPlanningWizardStates): void {
    switch (this._activeWizardStep) {
      case FwPlanningWizardStates.Selection:
        this.selectionData = this.wizardSelectionProvider.onLeave();
        break;
      case FwPlanningWizardStates.Details:
        this.detailsData = this.wizardDetailsProvider.onLeave();
        break;
      case FwPlanningWizardStates.Overview:
        this.overviewData = this.wizardOverviewProvider.onLeave();
        break;
    }
    const oldState = this._activeWizardStep;
    this._activeWizardStep = newState;
    this.dynComponent.saveGridDescriptors(oldState);
    if (this._stateModel[oldState][newState]) {
      this._stateModel[oldState][newState](oldState);
    }
    this.dynComponent.loadGridDescriptors(newState);
  }

  /**
   * Initializes the selection step by building data items and preselecting fields
   */
  private initSelection(oldState: PpWizardStates): void {
    this.items$ = this.wizardSelectionProvider.buildDataItems();
    this.buildUi(this.wizardSelectionProvider);
    const selectedIds = oldState === PpWizardStates.Initial ?
      this.fieldStore.getSelectedFieldIds() :
      this.selectionData?.map(s => s.Id?.toString());
    if (selectedIds?.length > 0) {
      setTimeout(() => this.dynComponent.dynGrid?.gridPaging?.setSelectedKeys(selectedIds), 0);
    }
  }

  /**
   * Initializes the details step by building form data and triggering validations
   */
  private initDetails(_oldState: PpWizardStates): void {
    this.detailsData = this.wizardDetailsProvider.buildItem(this.selectionData);
    setTimeout(() => this.dynComponent.dynForm.form.patchValue(this.detailsData), 0);
    this.buildUi(this.wizardDetailsProvider);
    setTimeout(() => this.dynComponent.dynForm.markAsTouched(), 0);
  }

  /**
   * Initializes the overview step by building data items from selection and details
   */
  private initOverview(_oldState: PpWizardStates): void {
    this.items$ = this.wizardOverviewProvider.buildDataItems(this.selectionData, this.detailsData);
    this.buildUi(this.wizardOverviewProvider);
  }

  /**
   * Saves plan books and closes the form
   */
  private initSave(): void {
    const planBooks = this.overviewData.map(x => x.PlanBook);
    this.fieldWorkPlanBooksStore.savePlanBooks(planBooks);
    setTimeout(() => this.formStore.closeForm(), 1);
  }

  /**
   * Closes the form when leaving the wizard
   */
  private initLeave(): void {
    setTimeout(() => this.formStore.closeForm(), 1);
  }

  /**
   * Updates UI components based on the current wizard provider
   */
  private buildUi(wizardProvider: IFwPlanPlanningWizardProvider<IFwPlanPlanningWizardSelectionData | IFwPlanPlanningWizardOverviewData, any>): void {
    if (!wizardProvider) {
      return;
    }
    wizardProvider.updateCaption();
    wizardProvider.updateFieldSets();
    this.columns$ = wizardProvider.getGridColumnConfig(this.detailsData);
    this.pager = wizardProvider.getPagerConfig(this.breadCrumbsColumns);
  }
}
